import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useStable } from "../context/StableContext";
import { useNavigation } from "../hooks/useNavigation";
import { ChevronDown, Check, PanelLeftClose, PanelLeftOpen, PanelLeftDashed } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";

const ROLE_LABEL = { admin: "Gestore", manager: "Gestore", staff: "Staff", owner: "Proprietario cavallo", readonly: "Lettura" };

// Three explicit, persisted sidebar modes.
export const SIDEBAR_MODES = [
  { key: "open",   icon: PanelLeftOpen,  label: "Aperta",             hint: "Sempre larga con etichette" },
  { key: "closed", icon: PanelLeftClose, label: "Chiusa",             hint: "Sempre compatta (solo icone)" },
  { key: "hover",  icon: PanelLeftDashed,label: "Chiusa a comparsa",  hint: "Compatta; si espande al passaggio del mouse" },
];
const MODE_META = Object.fromEntries(SIDEBAR_MODES.map((m) => [m.key, m]));

function SideNavItem({ item, showLabel }) {
  const { icon: Icon, to, label, end } = item;
  const cls = ({ isActive }) =>
    `w-full flex items-center rounded-lg text-sm font-medium transition-colors ${
      showLabel ? "gap-3 px-3 py-2" : "justify-center h-10 w-10 mx-auto"
    } ` +
    (isActive
      ? "bg-[#F0EAE1] text-[#1B3B2B]"
      : "text-[#5F6B64] hover:bg-[#F0EAE1]/60 hover:text-[#1B3B2B]");
  const link = (
    <NavLink to={to} end={end} data-testid={`nav-${label.toLowerCase()}`} className={cls}>
      <Icon className="h-[18px] w-[18px] shrink-0" />
      {showLabel && <span className="truncate">{label}</span>}
    </NavLink>
  );
  if (showLabel) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

function StableSwitcher({ showLabel }) {
  const { stables, current, select } = useStable();
  const navigate = useNavigate();
  const trigger = showLabel ? (
    <button data-testid="sidebar-stable-selector"
      className="w-full flex items-center gap-2 rounded-xl border border-[#E5DEC9] bg-white px-3 py-2 hover:border-[#8C532B] transition-colors text-left">
      <span className="min-w-0 flex-1">
        <span className="block font-mono-label text-[9px] uppercase tracking-wide text-[#9AA39C]">Scuderia</span>
        <span className="block font-semibold text-sm text-[#1B3B2B] truncate">{current?.name}</span>
      </span>
      <ChevronDown className="h-4 w-4 text-[#8C532B] shrink-0" />
    </button>
  ) : (
    <button data-testid="sidebar-stable-selector" aria-label={current?.name}
      className="h-10 w-10 mx-auto rounded-xl border border-[#E5DEC9] bg-white flex items-center justify-center font-semibold text-sm text-[#1B3B2B] hover:border-[#8C532B] transition-colors">
      {(current?.name || "S").slice(0, 1).toUpperCase()}
    </button>
  );
  return (
    <DropdownMenu>
      {showLabel ? (
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">{current?.name}</TooltipContent>
        </Tooltip>
      )}
      <DropdownMenuContent align="start" className="w-64">
        {stables.map((s) => (
          <DropdownMenuItem key={s.id} data-testid={`sidebar-stable-option-${s.id}`}
            onClick={() => select(s)} className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="font-medium">{s.name}</span>
              <span className="text-xs text-[#5F6B64]">{ROLE_LABEL[s.role]}</span>
            </div>
            {s.id === current?.id && <Check className="h-4 w-4 text-[#8C532B]" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="sidebar-manage-stables" onClick={() => navigate("/scuderie")}>
          Gestisci scuderie…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Desktop sidebar = navigazione dei moduli della scuderia.
// Three explicit, persisted modes:
//   - open   : always wide (~240px), icons + labels.
//   - closed : always compact rail (~68px); hover does NOT expand it.
//   - hover  : compact rail (~68px) that temporarily expands to full width as an OVERLAY
//              on mouse-enter (z-50 + shadow, no layout shift) and collapses on mouse-leave.
function ModeSwitcher({ mode, onSetMode, expanded }) {
  const Current = MODE_META[mode]?.icon || PanelLeftClose;
  const trigger = (
    <button data-testid="sidebar-mode-trigger"
      aria-label={`Modalità barra laterale: ${MODE_META[mode]?.label || ""}`}
      className={`w-full flex items-center rounded-lg text-sm font-medium transition-colors text-[#5F6B64] hover:bg-[#F0EAE1]/60 hover:text-[#1B3B2B] ${
        expanded ? "gap-3 px-3 py-2" : "justify-center h-10 w-10 mx-auto"
      }`}>
      <Current className="h-[18px] w-[18px] shrink-0" />
      {expanded && <span className="truncate">Barra laterale</span>}
    </button>
  );
  return (
    <DropdownMenu>
      {expanded ? (
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">Barra laterale: {MODE_META[mode]?.label}</TooltipContent>
        </Tooltip>
      )}
      <DropdownMenuContent side="top" align="start" className="w-60">
        <DropdownMenuLabel>Barra laterale</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SIDEBAR_MODES.map((m) => (
          <DropdownMenuItem key={m.key} data-testid={`sidebar-mode-${m.key}`}
            onClick={() => onSetMode(m.key)} className="flex items-start gap-2">
            <m.icon className="h-4 w-4 mt-0.5 shrink-0 text-[#8C532B]" />
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-[#1B3B2B]">{m.label}</span>
              <span className="block text-[11px] text-[#5F6B64]">{m.hint}</span>
            </span>
            {mode === m.key && <Check className="h-4 w-4 text-[#8C532B] shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function DesktopSidebar({ mode, onSetMode }) {
  const { mainGroups, utilityItems } = useNavigation();
  const [hovered, setHovered] = useState(false);

  const expanded = mode === "open" || (mode === "hover" && hovered);  // visually expanded (labels shown)
  const hoverOverlay = mode === "hover" && hovered;                    // temporary overlay expansion

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="desktop-sidebar"
        data-mode={mode}
        data-expanded={expanded ? "true" : "false"}
        onMouseEnter={() => { if (mode === "hover") setHovered(true); }}
        onMouseLeave={() => setHovered(false)}
        className={`hidden lg:flex flex-col fixed left-0 top-16 h-[calc(100vh-4rem)] z-40 border-r border-[#E5DEC9] bg-[#FAF8F5] transition-[width] duration-200 ${
          expanded ? "w-60" : "w-[68px]"
        } ${hoverOverlay ? "shadow-2xl" : ""}`}
      >
        {/* Context switcher */}
        <div className="px-3 py-3 border-b border-[#E5DEC9] shrink-0">
          <StableSwitcher showLabel={expanded} />
        </div>

        {/* Navigation groups (modules) */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
          {mainGroups.map((g, gi) => (
            <div key={g.key}>
              {expanded
                ? <p data-testid={`sidebar-group-${g.key}`} className="px-3 pb-1 font-mono-label text-[10px] uppercase tracking-wide text-[#9AA39C]">{g.label}</p>
                : gi > 0 && <div className="mx-2 mb-2 h-px bg-[#E5DEC9]" />}
              <div className="space-y-0.5">
                {g.items.map((it) => <SideNavItem key={it.key} item={it} showLabel={expanded} />)}
              </div>
            </div>
          ))}
        </nav>

        {/* Utility / system area (Impostazioni) + sidebar mode switcher */}
        <div className="border-t border-[#E5DEC9] px-2 py-2 space-y-0.5 shrink-0">
          {utilityItems.map((it) => <SideNavItem key={it.key} item={it} showLabel={expanded} />)}
          <ModeSwitcher mode={mode} onSetMode={onSetMode} expanded={expanded} />
        </div>
      </aside>
    </TooltipProvider>
  );
}
